import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { supabase } from "../lib/supabaseClient";
import AuthScreenHeader from "./AuthScreenHeader";
import {
  RESEND_COOLDOWN_SECONDS,
  isOtpExpiredError,
  isRateLimitError,
  parseRetrySeconds,
} from "../lib/otp";

// The enter-your-6-digit-code step both signup flows land on
// (confirmation hotfix: codes replace scanner-vulnerable links).
// Copy stays calm and non-technical — volunteers are parishioners,
// many older; "code didn't work" beats "otp_expired".
interface Props {
  email: string;
  // Called with a session established — the caller decides where to
  // land (volunteer dashboard / wizard continues).
  onVerified: () => void;
  // "Use a different email" escape hatch back to the signup form.
  onBack: () => void;
  brand?: ReactNode;
  // Seconds the resend button starts locked for. Callers pass the
  // server's own retry-after when signup said "recently sent";
  // otherwise the code was JUST sent, so a fresh full cooldown.
  initialCooldown?: number;
  footer?: ReactNode;
}

export default function ConfirmCodeStep({
  email,
  onVerified,
  onBack,
  brand,
  initialCooldown = RESEND_COOLDOWN_SECONDS,
  footer,
}: Props) {
  const [code, setCode] = useState("");
  const [status, setStatus] = useState<"idle" | "checking" | "error">("idle");
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(initialCooldown);
  const [resendNote, setResendNote] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setCooldown((c) => (c > 0 ? c - 1 : 0));
    }, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!supabase) return;
    const token = code.replace(/\D/g, "");
    // Supabase's email OTP length is a project setting (this project
    // currently issues 8-digit codes; the platform allows 6-10).
    // Never pin an exact length here - a valid code the input refuses
    // is the same dead-end this hotfix exists to kill.
    if (token.length < 6 || token.length > 10) {
      setStatus("error");
      setError("That doesn't look like a complete code. Copy the code from the email and try again.");
      return;
    }
    setStatus("checking");
    setError(null);
    const { error: verifyError } = await supabase.auth.verifyOtp({
      email,
      token,
      type: "signup",
    });
    if (verifyError) {
      setStatus("error");
      setError(
        isOtpExpiredError(verifyError.message)
          ? "That code didn't work. It may have expired: use the code from the newest email, or tap Send a new code below."
          : verifyError.message,
      );
      return;
    }
    onVerified();
  }

  async function resend() {
    if (!supabase || cooldown > 0) return;
    setResendNote(null);
    setError(null);
    setStatus("idle");
    const { error: resendError } = await supabase.auth.resend({ type: "signup", email });
    if (resendError && isRateLimitError(resendError.message)) {
      setCooldown(parseRetrySeconds(resendError.message) ?? RESEND_COOLDOWN_SECONDS);
      setResendNote("A code was sent just a moment ago. Give it a minute to arrive.");
      return;
    }
    if (resendError) {
      setStatus("error");
      setError(resendError.message);
      return;
    }
    // Note: a silent success here for an already-confirmed account
    // sends nothing (Supabase enumeration protection) — those
    // accounts never reach this step through our flows.
    setCooldown(RESEND_COOLDOWN_SECONDS);
    setResendNote("A new code is on its way. The newest code is the one that works.");
    setCode("");
  }

  return (
    <main className="page page--sign-in">
      <div className="sign-in">
        <AuthScreenHeader
          brand={brand}
          title="Enter your code"
          subtitle={`We emailed a confirmation code to ${email}. It should arrive within a minute; check your spam folder if you don't see it.`}
        />

        <form className="sign-in__form" onSubmit={submit}>
          <label className="sign-in__field">
            <span className="sign-in__label">Confirmation code</span>
            <input
              type="text"
              className="sign-in__input"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={10}
              placeholder="Code from your email"
              required
              autoFocus
            />
          </label>
          <button type="submit" className="btn" disabled={status === "checking"}>
            {status === "checking" ? "Checking…" : "Confirm"}
          </button>
          {error ? <p className="sign-in__error">{error}</p> : null}
          {resendNote ? <p className="sign-in__hint">{resendNote}</p> : null}
        </form>

        <div className="sign-in__alt">
          <button
            type="button"
            className="sign-in__link"
            onClick={() => void resend()}
            disabled={cooldown > 0}
          >
            {cooldown > 0 ? `Send a new code (${cooldown}s)` : "Send a new code"}
          </button>
        </div>
        <div className="sign-in__alt">
          <button type="button" className="sign-in__link" onClick={onBack}>
            Use a different email
          </button>
        </div>
      </div>
      {footer}
    </main>
  );
}
